import {
	FunctionComponent,
	ReactElement,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useUserTimezone,
	useWarnBeforeUnload,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	BusyIndicator,
	IconButtonWithTitle,
	NoResults,
	useConfirmationDialog
} from "client/Components";
import { PostViewer } from "./PostViewer";
import { never } from "client_legacy/ndaw/errors";
import { CommunityViewModel, deleteCommunityPost } from "client/Api/Communities/communityPosts";
import {
	connectToRouter,
	NavigationCallback,
	PredefinedPropOptionKind,
	QueryParametersFromProps,
	RouteParametersFromProps
} from "client/Navigation";
import { tryParseCommunityId, getHubNavigationForCommunity } from "./CommunitiesPage";
import { HubDataSource } from "client/Api/Hub/hub";
import { HubLayoutContext } from "../HubLayout";
import { CommunityNodeId } from "../Sidebar/HubSidebar";
import {
	isNil,
	resolveFormattedTextString,
	setTrackingId
} from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { tryParseInt } from "client/Misc/parseUtils";
import styles from "./CommunityPostPage.module.less";
import { useTitleBarNavigationStore } from "client/Features/Layout/TitleBar/titleBarNavigationStore";
import { CommunityId } from "./CommunityFeedPage";
import { CommunityPostPageCommentSection } from "./CommunityPostPageCommentSection";
import { TimeZone } from "client/DateAndTime/timezones";
import { useCommunityPostContent } from "../HubCommentSection/hooks/useCommunityPostContent";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

export type CommunityPostPageProps = {
	id: number;
	communityId: number | undefined | "my-feed";
	onNavigate: NavigationCallback;
};

export const CommunityPostPage = (props: CommunityPostPageProps): ReactElement => {
	const {
		id,
		communityId,
		onNavigate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [isEditingMobileComment, setIsEditingMobileComment] = useState<boolean>(false);

	const commentSectionRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const userTimezone: TimeZone = useUserTimezone();
	useWarnBeforeUnload(isEditingMobileComment);

	const {
		changeActiveSidebarItem
	} = useContext(HubLayoutContext);

	const navigateToCommunityHandler = useCallback(
		(communityId: Exclude<CommunityNodeId, undefined>): void => {
			onNavigate(
				getHubNavigationForCommunity({
					source: HubDataSource.Community,
					communityId: communityId
				})
			);
		},
		[onNavigate]
	);

	const firstFetchDoneHandler = useCallback(
		(): void => {
			const hashFragment: string | null = window
				.location
				.hash || null;

			if (hashFragment === `#${COMMUNITY_POST_COMMENT_SECTION_FRAGMENT}`) {
				// We need timeout to invoke the
				// the scrolling after rendering of
				// the DOM of the page
				setTimeout(
					(): void => {
						commentSectionRef.current
							?.scrollIntoView({
								behavior: "smooth",
								block: "start"
							});
					},
					1
				);
			}
		},
		[]
	);

	const {
		reloadPostHandler,
		isFollowingChangedHandler,
		postState,
		retrievePostStatistics,
		statisticsRetrievedHandler
	} = useCommunityPostContent(
		id,
		firstFetchDoneHandler
	);

	const communityNavigationTarget = useMemo(
		(): CommunityNavigationTarget => {
			if (typeof communityId !== "number" || postState.loadingStatus !== "loaded") {
				return MY_FEED_COMMUNITY;
			}

			const postCommunity: CommunityViewModel= postState.post
				.community;

			return communityId === postCommunity.id
				? postCommunity
				: MY_FEED_COMMUNITY;
		},
		[postState, communityId]
	);

	const backNavigationHandler = useCallback(
		(): void => {
			navigateToCommunityHandler(communityNavigationTarget.id);
		},
		[navigateToCommunityHandler, communityNavigationTarget]
	);

	const postCommunityId = useMemo(
		(): number | undefined => {
			return postState.loadingStatus !== "loaded"
				? undefined
				: postState.post
					.community
					.id;
		},
		[postState]
	);

	useEffect(
		(): void => {
			if (isNil(postCommunityId)) {
				return;
			}

			changeActiveSidebarItem({
				source: HubDataSource.Community,
				communityId: postCommunityId
			});
		},
		[
			communityId,
			changeActiveSidebarItem,
			postCommunityId
		]
	);

	const deletePostHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<void> = await openConfirmationDialog({
				trackingId: "delete-post-confirmation-dialog",
				title: localize("_AreYouSureYouWantDoDeleteThisPost"),
				confirmText: localize("_Delete"),
				cancelText: localize("_Cancel")
			});

			if (!result.wasSuccessful) {
				return;
			}

			setIsBusy(true);

			try {
				await deleteCommunityPost(id, globalCancellation);
				backNavigationHandler();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			backNavigationHandler,
			globalCancellation,
			id,
			openConfirmationDialog
		]
	);

	const renderContent = (): ReactElement => {
		switch (postState.loadingStatus) {
			case "loading":
				return (
					<BusyIndicator />
				);
			case "error":
				return (
					<NoResults
						trackingId="has-loading-error"
						icon="gear"
						title={localize("_ItemNotFound")}
						message={localize("_WeWereUnableToLoadTheItem")}
						onRetry={reloadPostHandler}
						height="fill"
					/>
				);
			case "not-found":
				return (
					<NoResults
						trackingId="item-not-found"
						icon="users"
						title={localize("_ItemNotFound")}
						message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
						onRetry={backNavigationHandler}
						height="fill"
						retryText={resolveFormattedTextString({
							template: localize("_BackToName"),
							replacements: [
								{
									what: "{{Name}}",
									filler: communityNavigationTarget.name
								}
							]
						})}
					/>
				);
			case "loaded":
				const { post, options } = postState;

				return (
					<>
						<div
							{...setTrackingId("community-post-page")}
							className={styles.content}
						>
							<PostViewer
								post={post}
								onAfterUpdate={reloadPostHandler}
								onIsFollowingChanged={isFollowingChangedHandler}
								onDelete={deletePostHandler}
								postOptions={options}
								onStatisticsRetrieved={statisticsRetrievedHandler}
								onNavigateToCommunity={
									(): void => navigateToCommunityHandler(
										post.community
											.id
									)
								}
							/>
							<CommunityPostPageCommentSection
								ref={commentSectionRef}
								options={options}
								onMobileCommentIsEditingChanged={setIsEditingMobileComment}
								onPostStatisticsUpdate={retrievePostStatistics}
								postId={id}
								timezone={userTimezone}
								totalCommentsCount={
									post.statistics
										.commentsCount
								}
							/>
						</div>
						<AiAssistantFabSpacing />
					</>
				);
			default:
				never(postState);
		}
	};

	return (
		<>
			<div className={styles.root}>
				{isBusy && (
					<BusyIndicator />
				)}

				{postState.loadingStatus !== "loading" && (
					<BackButton
						onBack={backNavigationHandler}
						text={communityNavigationTarget.name}
					/>
				)}
				{renderContent()}
			</div>

			{confirmationDialog}
		</>
	);
};
CommunityPostPage.displayName="CommunityPostPage";

export const COMMUNITY_POST_COMMENT_SECTION_FRAGMENT = "comments";

type CommunityNavigationTarget = CommunityViewModel
	| {
		id: CommunityId;
		name: string;
	};

const MY_FEED_COMMUNITY: CommunityNavigationTarget = {
	id: "my-feed",
	name: localize("_MyFeed")
};

export type BackButtonProps = {
	text: string;
	onBack: () => void;
};

const BackButton = (
	props: BackButtonProps
): ReactElement | null => {
	const {
		text,
		onBack
	} = props;

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const {
		setBackNavigationCallback,
		restoreState,
		setIsBackButtonVisible
	} = useTitleBarNavigationStore(
		(x) => ({
			setBackNavigationCallback: x.setBackNavigationCallback,
			setIsBackButtonVisible: x.setIsBackButtonAvailable,
			restoreState: x.restoreState
		}),
		shallowComparison
	);

	useEffect(
		(): (() => void) | undefined => {
			if (!isResponsive) {
				return;
			}
			setIsBackButtonVisible(true);
			setBackNavigationCallback(onBack);

			return (): void => {
				restoreState();
			};
		},
		[
			onBack,
			setBackNavigationCallback,
			setIsBackButtonVisible,
			restoreState,
			isResponsive
		]
	);

	if (isResponsive) {
		return null;
	}

	return (
		<div className={styles.backContainer}>
			<IconButtonWithTitle
				title={text}
				icon="chevron-left"
				onClick={onBack}
				trackingId="page-content-back"
			/>
		</div>
	);
};
BackButton.displayName = "BackButton";

export type CommunityPostPageRouteParams = RouteParametersFromProps<
	CommunityPostPageProps,
	"id"
>;

export type CommunityPostPageQueryParams = QueryParametersFromProps<
	CommunityPostPageProps,
	"communityId"
>;

export const CommunityPostPageForRouter: FunctionComponent = connectToRouter(
	CommunityPostPage,
	{
		"id": {
			isRequired: true,
			tryParse: tryParseInt
		},
		"communityId": {
			isRequired: false,
			type: "query",
			tryParse: tryParseCommunityId,
		},
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);
